import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import React, { useEffect, useState } from "react";
import {
  createCustomerAccountMutation,
  facebookLoginMutation,
  loginAnyCustomerTypeMutation,
  loginMutation,
  loginPartnerMutation,
  logoutMutation,
  removeCustomerAccountMutation,
  updateCustomerAccountMutation,
} from "rsl-api-106/lib/mutations";
import {
  getCurrentCustomerAccountQuery,
  getPartnerQuery,
} from "rsl-api-106/lib/queries";
import {
  CriteriaValueInput,
  Mutation,
  MutationCreateCustomerAccountArgs,
  MutationFacebookLoginArgs,
  MutationLoginAnyCustomerTypeArgs,
  MutationLoginArgs,
  MutationLoginPartnerArgs,
  MutationLogoutArgs,
  MutationRemoveCustomerAccountArgs,
  MutationUpdateCustomerAccountArgs,
  Query,
  QueryGetCurrentCustomerAccountArgs,
  QueryGetPartnerArgs,
  Service,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Popup from "@components/_Layout/Controls/Popup";
import { LoginPartner } from "@components/Auth/Login/Login";
import { AuthContext } from "@contexts";
import { useCart, useSession } from "@hooks";
import dateFormat from "@utils/dateformat";
import { removeLocalStorageItem } from "@utils/localStorage";

export const AuthProvider: React.FC = ({ children }) => {
  const client = useApolloClient();
  const { sessionId, setSessionId, authMode, getLanguageVarValue } =
    useSession();
  const { cart, updateOccupant } = useCart();
  const [user, setUser] = useState<Service | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const abortController = new AbortController();
    if (sessionId) {
      if (authMode === 2) {
        client
          .query<Query, QueryGetPartnerArgs>({
            query: getPartnerQuery(),
            variables: {
              session: { name: sessionId },
            },
            context: {
              fetchOptions: {
                signal: abortController.signal,
              },
            },
            fetchPolicy: "no-cache",
          })
          .then((response) => {
            if (response.data?.getPartner?.service) {
              setUser(response.data.getPartner?.service);
            }
          })
          .catch((err: GraphQLError) => {
            setUser(null);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        client
          .query<Query, QueryGetCurrentCustomerAccountArgs>({
            query: getCurrentCustomerAccountQuery(),
            variables: {
              session: { name: sessionId },
            },
            context: {
              fetchOptions: {
                signal: abortController.signal,
              },
            },
            fetchPolicy: "no-cache",
          })
          .then((response) => {
            if (response.data?.getCurrentCustomerAccount?.service) {
              setUser(response.data.getCurrentCustomerAccount?.service);
            }
          })
          .catch((err: GraphQLError) => {
            setUser(null);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }

    return () => {
      abortController.abort();
    };
  }, [sessionId]);

  /**
   * It calls the GraphQL mutation login() and passes in the username, password, and a callback
   * function.
   * @param {string} username - The username of the user.
   * @param {string} password - The password of the user.
   * @param callback - A function that will be called when the mutation is complete.
   */
  const login = (
    login: string,
    password: string,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationLoginArgs>({
          mutation: loginMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              login: login,
              password: password,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.login?.service) {
            setUser(response.data.login.service);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It calls the GraphQL mutation loginAnyclientType() and passes in the username, password, and a callback
   * function.
   * @param {string} username - The username of the user.
   * @param {string} password - The password of the user.
   * @param callback - A function that will be called when the mutation is complete.
   */
  const loginAnyclientType = (
    login: string,
    password: string,
    callback: (service: Service | null, err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationLoginAnyCustomerTypeArgs>({
          mutation: loginAnyCustomerTypeMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              login: login,
              password: password,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.loginAnyCustomerType?.service) {
            const u = response.data.loginAnyCustomerType.service;
            setUser(u);
            if (
              cart &&
              cart.reservations &&
              (cart.reservations.length ?? -1) > 0 &&
              cart.reservations[0]?.primaryOccupant &&
              isEmpty(cart.reservations[0]?.primaryOccupant.firstName)
            ) {
              const resa = cart.reservations[0];
              updateOccupant(
                resa?.stays?.[0]?.occupants?.[0]?.guiid ?? 0,
                response.data.loginAnyCustomerType.service.firstName ?? "",
                response.data.loginAnyCustomerType.service.lastName ?? "",
                response.data.loginAnyCustomerType.service.birthDate,
                (err) => {
                  if (!err) {
                    callback(u, undefined);
                  }
                }
              );
            } else {
              callback(u, undefined);
            }
          }
        })
        .catch((err: GraphQLError) => {
          callback(null, err);
        });
    }
  };

  const partnerLogin = (
    login: string,
    password: string,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationLoginPartnerArgs>({
          mutation: loginPartnerMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              login: login,
              password: password,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.loginPartner &&
            response.data.loginPartner.service
          ) {
            setUser(response.data.loginPartner.service);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It registers a new customer.
   * @param {string} username - The username of the customer.
   * @param {string} password - The password of the customer.
   * @param {string} passwordConfirm - The password confirmation.
   * @param {string} civility - The customer's civility.
   * @param {string} firstname - string,
   * @param {string} lastname - string,
   * @param {Date} birthdate - Date,
   * @param {string} languageCode - The language code of the user.
   * @param {string} streetName - The street name of the address.
   * @param {string} postalLocality - The postal locality is the city.
   * @param {string} addressComplementary1 - string,
   * @param {string} addressComplementary2 - string,
   * @param {string} zipCode - The postal code of the address.
   * @param {string} city - The city where the customer lives.
   * @param {string} country - The country of the customer.
   * @param {string} phone1 - The phone number of the customer.
   * @param {string} phone2 - string,
   * @param {string} fax - string,
   * @param {string} mail - The email address of the customer.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const register = (
    username: string,
    password: string,
    passwordConfirm: string,
    civility: string,
    firstname: string,
    lastname: string,
    birthdate: Date,
    languageCode: string,
    streetName: string,
    postalLocality: string,
    addressComplementary1: string,
    addressComplementary2: string,
    zipCode: string,
    city: string,
    country: string,
    phone1: string,
    phone2: string,
    fax: string,
    mail: string,
    criterias: CriteriaValueInput[],
    callback: (service: Service | null, err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      const formatedBirthdate = dateFormat(birthdate, "yyyy-mm-dd");
      client
        .mutate<Mutation, MutationCreateCustomerAccountArgs>({
          mutation: createCustomerAccountMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              account: {
                userName: username,
                password,
                password2: passwordConfirm,
              },
              civility: civility,
              firstName: firstname,
              lastName: lastname,
              birthDate: formatedBirthdate,
              languageCode,
              address: {
                streetName,
                postalLocality,
                addressComplementary1,
                addressComplementary2,
                zipCode,
                city,
                country,
                phone1,
                phone2,
                fax,
                mail,
              },
              criterias: criterias,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.createCustomerAccount?.service) {
            const u = response.data.createCustomerAccount.service;
            setUser(u);
            if (
              cart &&
              cart.reservations &&
              (cart.reservations.length ?? -1) > 0 &&
              cart.reservations[0]?.primaryOccupant &&
              isEmpty(cart.reservations[0]?.primaryOccupant.firstName)
            ) {
              const resa = cart.reservations[0];
              updateOccupant(
                resa?.stays?.[0]?.occupants?.[0]?.guiid ?? 0,
                response.data.createCustomerAccount.service.firstName ?? "",
                response.data.createCustomerAccount.service.lastName ?? "",
                response.data.createCustomerAccount.service.birthDate,
                (err) => {
                  if (!err) {
                    callback(u, undefined);
                  }
                }
              );
            } else {
              callback(u, undefined);
            }
          }
        })
        .catch((err) => {
          console.log(err);
          callback(null, err);
        });
    }
  };

  /**
   * Update the user's data
   * @param {string} username - The username of the customer.
   * @param {string} civility - The customer's civility.
   * @param {string} firstname - The first name of the customer.
   * @param {string} lastname - string,
   * @param {string} languageCode - The language code of the user.
   * @param {string} streetName - The street name of the customer's address.
   * @param {string} postalLocality - The postal locality is the city.
   * @param {string} addressComplementary1 - string,
   * @param {string} addressComplementary2 - string,
   * @param {string} zipCode - The postal code of the address.
   * @param {string} city - The city where the customer lives.
   * @param {string} country - The country of the customer.
   * @param {string} phone1 - The phone number of the customer.
   * @param {string} phone2 - string,
   * @param {string} mail - The email address of the customer.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const update = (
    username: string,
    password: string | null,
    passwordConfirm: string | null,
    civility: string,
    firstname: string,
    lastname: string,
    birthdate: Date,
    languageCode: string,
    streetName: string,
    postalLocality: string,
    addressComplementary1: string,
    addressComplementary2: string,
    zipCode: string,
    city: string,
    country: string,
    phone1: string,
    phone2: string,
    mail: string,
    criterias: CriteriaValueInput[],
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      const formatedBirthdate = dateFormat(birthdate, "yyyy-mm-dd");
      client
        .mutate<Mutation, MutationUpdateCustomerAccountArgs>({
          mutation: updateCustomerAccountMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              account: {
                userName: username,
                password: password ? password : undefined,
                password2: passwordConfirm ? passwordConfirm : undefined,
              },
              birthDate: formatedBirthdate,
              civility: civility,
              firstName: firstname,
              lastName: lastname,
              languageCode,
              address: {
                streetName,
                postalLocality,
                addressComplementary1,
                addressComplementary2,
                zipCode,
                city,
                country,
                phone1,
                phone2,
                mail,
              },
              criterias: criterias,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.updateCustomerAccount?.service) {
            setUser(response.data?.updateCustomerAccount?.service);
            callback();
          }
        })
        .catch((err) => {
          callback(err);
        });
    }
  };

  /**
   * It logs out the user by calling the GraphQL API.
   * @param callback - A callback function that is called when the query is complete.
   */
  const signout = (callback: (err?: GraphQLError) => void) => {
    if (sessionId) {
      client
        .query<Mutation, MutationLogoutArgs>({
          query: logoutMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        })
        .then(() => {
          setUser(null);
          //setSessionId(null);
          //removeLocalStorageItem("sessionId");
          callback();
        })
        .catch((err) => {
          callback(err);
        });
    }
  };

  /**
   * It deletes the current user from the database.
   * @param callback - A function that will be called when the mutation is complete.
   */
  const _delete = (callback: (err?: GraphQLError) => void) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveCustomerAccountArgs>({
          mutation: removeCustomerAccountMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeCustomerAccount?.removed) {
            setUser(null);
            removeLocalStorageItem("sessionId");
            callback();
          }
        })
        .catch((err) => {
          callback(err);
        });
    }
  };

  const facebookRegister = (
    facebookID: string,
    firstName: string,
    lastName: string,
    birthdate: Date,
    email: string,
    callback: (response?: Service, err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      console.log(birthdate);
      const formatedBirthdate = dateFormat(birthdate, "yyyy-mm-dd");
      client
        .mutate<Mutation, MutationCreateCustomerAccountArgs>({
          mutation: createCustomerAccountMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              account: {
                facebookID,
              },
              firstName,
              lastName,
              birthDate: formatedBirthdate,
              address: {
                mail: email,
              },
            },
          },
        })
        .then((response) => {
          if (response.data?.createCustomerAccount?.service) {
            callback(response.data.createCustomerAccount?.service, undefined);
          }
        })
        .catch((err) => {
          callback(undefined, err);
        });
    }
  };

  const facebookLogin = (
    facebookID: string,
    callback: (response?: Service, err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationFacebookLoginArgs>({
          mutation: facebookLoginMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              facebookID: facebookID,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.facebookLogin?.service) {
            callback(response.data.facebookLogin.service, undefined);
          }
        })
        .catch((err) => {
          callback(undefined, err);
        });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        loginAnyclientType,
        partnerLogin,
        register,
        update,
        signout,
        delete: _delete,
        facebookRegister,
        facebookLogin,
      }}
    >
      {!loading && authMode === 2 && !user ? (
        <Popup
          title={getLanguageVarValue("LANG_neobooking_login_partner")}
          enableClose={false}
          btnElement={(onClick) => <></>}
          open={true}
        >
          <LoginPartner />
        </Popup>
      ) : (
        <></>
      )}
      {children}
    </AuthContext.Provider>
  );
};
